import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import moment from "moment";
import {
  AddressInput,
  DateCalendarForm,
  InputMain,
  PhoneInput,
} from "components";
import { Stepper } from "./stepper";
import {
  bankAccountActions,
  httpRequestsOnErrorsActions,
  httpRequestsOnSuccessActions,
} from "store";
import {
  EmailValidator,
  FindError,
  FindLoad,
  FindSuccess,
  ProfileInfo,
  SaveParams,
} from "utils";
import { BUSINESSTYPE } from "./constants";

const PAYMENT_ACCOUNT = "CONNECT_NON_PROFIT";

export const PersonalDetails = () => {
  const location = useLocation();
  const navigator = useNavigate();
  const dispatch = useDispatch();
  const { handleSubmit, control, clearErrors, reset, setError } = useForm();
  const { myProfile } = ProfileInfo();
  const info = location?.state;

  const personLoading = FindLoad(PAYMENT_ACCOUNT);
  const personSuccess = FindSuccess(PAYMENT_ACCOUNT);
  const personBeckError = FindError(PAYMENT_ACCOUNT);

  useEffect(() => {
    if (info?.personalDetails) {
      reset(info?.personalDetails);
    }
  }, [info]);

  useEffect(() => {
    if (
      personBeckError?.error === "Invalid address format or location not found."
    ) {
      setError("address", {
        message: "Invalid address format or location not found",
      });
    }

    if (
      personBeckError?.error === "Something went wrong while converting address"
    ) {
      setError("address", {
        message: "Something went wrong while converting address",
      });

      dispatch(httpRequestsOnErrorsActions.removeError(PAYMENT_ACCOUNT));
    }
  }, [personBeckError]);

  useEffect(() => {
    if (personSuccess?.type) {
      const params = {
        ...info,
        step: 6,
      };

      SaveParams(location?.pathname, navigator, params);
      dispatch(httpRequestsOnSuccessActions.removeSuccess(PAYMENT_ACCOUNT));
    }
  }, [personSuccess]);

  useEffect(() => {
    return () => {
      clearErrors();
      dispatch(httpRequestsOnErrorsActions.removeError(PAYMENT_ACCOUNT));
    };
  }, []);

  const handleCreate = (data) => {
    const params = {
      ...info,
    };

    const dob = new Date(data?.birthDate);
    const sandData = {
      email: myProfile?.email,
      businessType: BUSINESSTYPE,
      individual: {
        ...data,
        phone: parseInt(data.phone.replace(/[^0-9]/g, "")),
        address: data?.address,
        dob: {
          day: dob.getDate(),
          month: dob.getMonth() + 1,
          year: dob.getFullYear(),
        },
      },
    };

    delete sandData?.individual?.birthDate;

    dispatch(bankAccountActions.connectNonProfit(sandData));

    params.personalDetails = {
      ...data,
    };
    SaveParams(location?.pathname, navigator, params);
  };

  return (
    <form className="in-celebration-box" onSubmit={handleSubmit(handleCreate)}>
      <div className="in-celebration-content">
        <div className="container">
          <div className="in-celebration-form-wrapper personal-details-wrapper">
            <div className="in-celebration-form-header">
              <p className="in-celebration-form-header-title">
                Personal Details
              </p>
              <p className="in-celebration-form-header-desc">
                You will need to provide your personal banking information for
                receive the other half.
              </p>
            </div>

            <div className="in-celebration-form-content">
              <InputMain
                className="celebration-input"
                name="firstName"
                control={control}
                placeholder="Enter your first name"
                labelHeader="First Name"
                rules={{
                  required: "First name is required",
                  validate: {
                    trimmed: (value) =>
                      value.trim() !== "" ||
                      "First name cannot be empty or just spaces",
                  },
                }}
              />

              <InputMain
                className="celebration-input"
                name="lastName"
                control={control}
                placeholder="Enter your last name"
                labelHeader="Last Name"
                rules={{
                  required: "Last name is required",
                  validate: {
                    trimmed: (value) =>
                      value.trim() !== "" ||
                      "Last name cannot be empty or just spaces",
                  },
                }}
              />

              <DateCalendarForm
                className="celebration-input"
                name="birthDate"
                placeholder="dd/mm/yyyy"
                control={control}
                label="Date of Birth*"
                rules={{
                  required: "Date of Birth is required",
                  validate: (value) => {
                    const selectedDate = moment(value);
                    const currentDate = moment();
                    const age = currentDate.diff(selectedDate, "years");
                    return (
                      age >= 13 ||
                      "You must be at least 13 years old to use Stripes"
                    );
                  },
                }}
              />

              <InputMain
                className="celebration-input"
                name="email"
                type="email"
                control={control}
                placeholder="Enter your personal email address"
                labelHeader="Email Address"
                rules={{
                  required: "Email Address is required",
                  pattern: {
                    value: EmailValidator,
                    message: "Must be valid email",
                  },
                }}
              />

              <PhoneInput
                className="celebration-input"
                placeholder="Enter your contact number"
                name="phone"
                control={control}
                labelHeader="Phone Number"
                rules={{
                  required: "Phone Number is required",
                  minLength: {
                    value: 10,
                    message: "Please enter a valid 10 digit number.",
                  },
                  validate: (value) => {
                    return (
                      value?.replace(/[^0-9]/g, "").length === 10 ||
                      "Please enter a valid 10 digit number."
                    );
                  },
                }}
              />

              <InputMain
                className="celebration-input"
                name="idNumber"
                type="number"
                control={control}
                labelHeader="ID Number"
                placeholder="Enter government-issued ID number"
                maxLength={9}
                rules={{
                  required: "ID Number is required",
                  minLength: {
                    value: 9,
                    message: "ID numbers must be 9 characters long",
                  },
                  maxLength: {
                    value: 9,
                    message: "ID numbers must be 9 characters long",
                  },
                }}
              />
              <AddressInput
                className="celebration-input"
                name="address"
                control={control}
                labelHeader="Address"
                placeholder="Address"
                rules={{
                  required: "Address is required",
                }}
              />
              <p className="error-message" style={{ fontSize: 14 }}>
                {personBeckError?.error}
              </p>
            </div>
          </div>
        </div>
      </div>
      <Stepper loading={personLoading} />
    </form>
  );
};
